import React, { useMemo } from "react";
import { Grid, SvgIcon } from "@mui/material";
import { StriimButton, StriimFeedbackBlock, StriimTypography } from "@striim/striim-ui";

import { styles } from "./selectTables.styles";
import { InfoEdit, Retry } from "src/generic/icon/customIcons";
import Click from "/app/images/striimline/click.svg";
import { useSelectTables } from "./select-tables-context";
import utils from "core/utils";
import WithTooltip from "src/generic/tooltip/tooltip";
import { isAppAdapter } from "src/modules/wizards/utils/checkAdapterType";
import { getFormattedDate, getStatusText } from "./selectTables-utils";
import { TestConnectionStatus } from "src/generic/test-connection/test-connection";
import { Tabs } from "./selectTables";

interface InitialViewInterface {
    enableAction: boolean;
    onClickAction: () => void;
    entityType: string;
    schemaType: string;
    adapterName: string;
    tablesError?: string;
    errorDescription?: string;
    disabled?: boolean;
    areConnectionFieldsValid: boolean;
}

interface ErrorViewInterface {
    error: { timestamp: number; message: string } | null;
    onRetry: () => void;
    enableAction: boolean;
    tooltip?: string;
    disabled?: boolean;
}

interface SuccessViewInterface {
    onEdit: () => void;
    enableAction: boolean;
    tooltip?: string;
    disabled?: boolean;
}

interface WarningViewInterface {
    onCancel: () => void;
    onEdit: () => void;
    entityType: string;
    enableAction: boolean;
    tooltip?: string;
    disabled?: boolean;
    previousTab: Tabs | undefined;
}

interface ContentVisualSelectionInterface {
    enableAction: boolean;
    onIncludeTablesChange: (value: string) => void;
    onClickAction: () => void;
    success: null | boolean;
    onEdit: () => void;
    entityType: string;
    schemaType: string;
    adapterName: string;
    showWarning: boolean;
    tablesError?: string;
    error: { timestamp: number; message: string } | null;
    previousTab: Tabs | undefined;
    onSwitchToVisualMode: () => void;
    areConnectionFieldsValid: boolean;
    testConnectionStatus: TestConnectionStatus;
    onCancellingModeSwitch: () => void;
}

const InitialView: React.FC<InitialViewInterface> = ({
    enableAction,
    onClickAction,
    entityType,
    schemaType,
    adapterName,
    tablesError,
    errorDescription,
    disabled,
    areConnectionFieldsValid
}) => (
    <Grid container flexDirection={"column"}>
        {!areConnectionFieldsValid && (
            <Grid item mb={2}>
                <StriimFeedbackBlock
                    variant="warning"
                    content={
                        <StriimTypography variant="body4" color={"greyscale.900"} fontFamily={"Inter"}>
                            You can enable visual table selection by configuring connection properties.
                        </StriimTypography>
                    }
                />
            </Grid>
        )}
        <Grid item>
            <StriimTypography variant="body4" display="inline" color="greyscale.900" data-test-id="initial-view-info">
                {!errorDescription
                    ? `Select ${!isAppAdapter(adapterName) ? `${schemaType}s and` : ""} ${entityType}s visually.`
                    : null}
            </StriimTypography>
        </Grid>
        <Grid item mt={1}>
            {!!tablesError
                ? WithTooltip(
                      <StriimFeedbackBlock
                          variant="critical"
                          content={
                              <Grid container gap={1}>
                                  <StriimTypography variant="body4" color="greyscale.900">
                                      Select at least one {entityType} to continue.
                                  </StriimTypography>
                              </Grid>
                          }
                          footer={
                              <StriimButton
                                  variant="text"
                                  disabled={!enableAction || disabled}
                                  startIcon={
                                      <SvgIcon
                                          component={Click}
                                          sx={[{ fill: "none", ml: -0.5 }, disabled ? styles.iconDisabled : undefined]}
                                      />
                                  }
                                  sx={styles.footerButton}
                                  onClick={onClickAction}
                              >
                                  Select {utils.capitalize(entityType)}s
                              </StriimButton>
                          }
                          customStyles={styles.customBlock}
                      />,
                      errorDescription
                  )
                : WithTooltip(
                      <StriimButton variant="secondary" disabled={!enableAction || disabled} onClick={onClickAction}>
                          Select {utils.capitalize(entityType)}s
                      </StriimButton>,
                      errorDescription
                  )}
        </Grid>
    </Grid>
);

const ErrorView: React.FC<ErrorViewInterface> = ({ error, onRetry, enableAction, tooltip, disabled }) => {
    const { currentSelection, sourceInfo } = useSelectTables();
    let statusText;

    if (currentSelection?.schemas) {
        statusText = getStatusText(sourceInfo, currentSelection);
    }
    return (
        <StriimFeedbackBlock
            variant="critical"
            header={getFormattedDate(error.timestamp)}
            content={
                <Grid container gap={1}>
                    {!!statusText && (
                        <StriimTypography variant="body4" color="greyscale.900" sx={{ mb: 1 }}>
                            {statusText}
                        </StriimTypography>
                    )}
                    <StriimTypography variant="body4" color="greyscale.900">
                        {currentSelection?.schemas
                            ? "Unable to connect to the source. Check connection properties or continue in List mode with the current selections."
                            : error.message}
                    </StriimTypography>
                </Grid>
            }
            footer={
                enableAction
                    ? WithTooltip(
                          <StriimButton
                              variant="text"
                              disabled={disabled}
                              startIcon={
                                  <SvgIcon
                                      component={Retry}
                                      sx={[styles.retryIcon, disabled ? styles.iconDisabled : undefined]}
                                  />
                              }
                              sx={styles.footerButton}
                              onClick={onRetry}
                          >
                              Retry
                          </StriimButton>,
                          tooltip
                      )
                    : null
            }
            customStyles={styles.customBlock}
        />
    );
};

const SuccessView: React.FC<SuccessViewInterface> = ({ onEdit, enableAction, disabled, tooltip }) => {
    const { currentSelection, sourceInfo } = useSelectTables();

    const text = getStatusText(sourceInfo, currentSelection);

    return (
        <StriimFeedbackBlock
            variant="success"
            header={getFormattedDate(currentSelection.timestamp)}
            content={
                <Grid container gap={1}>
                    <StriimTypography variant="body4" color="greyscale.900" data-test-id="selected-schema-info">
                        {text}
                    </StriimTypography>
                </Grid>
            }
            footer={WithTooltip(
                <StriimButton
                    variant="text"
                    disabled={disabled || !enableAction}
                    startIcon={
                        <SvgIcon
                            component={InfoEdit}
                            sx={[styles.editIcon, disabled || !enableAction ? styles.iconDisabled : undefined]}
                        />
                    }
                    sx={styles.footerButton}
                    onClick={onEdit}
                >
                    Edit
                </StriimButton>,
                tooltip
            )}
            customStyles={styles.customBlock}
        />
    );
};

const WarningView: React.FC<WarningViewInterface> = ({
    onEdit,
    onCancel,
    entityType,
    enableAction,
    disabled,
    tooltip,
    previousTab
}) => {
    return (
        <StriimFeedbackBlock
            variant="info"
            content={
                <Grid container gap={1}>
                    <StriimTypography variant="body4" color="greyscale.900">
                        {previousTab === Tabs.manual
                            ? `You have already selected some ${entityType.toLowerCase()}s in list mode. If you switch to the
                        visual mode, you will lose your previous selections.`
                            : `You have already selected some ${entityType.toLowerCase()}s using a query. If you switch to visual mode, you will lose your previous selections.`}
                    </StriimTypography>
                </Grid>
            }
            footer={
                <Grid container>
                    <Grid item>
                        {WithTooltip(
                            <StriimButton
                                variant="text"
                                disabled={!enableAction || disabled}
                                sx={styles.footerButton}
                                onClick={onEdit}
                                data-testid="switch-to-visual-selection-button"
                            >
                                Switch to Visual Mode
                            </StriimButton>,
                            tooltip
                        )}
                    </Grid>
                    <Grid item>
                        <StriimButton
                            variant="text"
                            disabled={!enableAction}
                            sx={[styles.footerButton, styles.cancelButton]}
                            onClick={onCancel}
                        >
                            Cancel
                        </StriimButton>
                    </Grid>
                </Grid>
            }
            customStyles={styles.customBlock}
        />
    );
};

const ContentVisualSelection: React.FC<ContentVisualSelectionInterface> = ({
    enableAction,
    onIncludeTablesChange,
    onClickAction,
    success = false,
    onEdit,
    entityType,
    schemaType,
    adapterName,
    showWarning,
    tablesError,
    error,
    previousTab,
    onSwitchToVisualMode,
    areConnectionFieldsValid,
    testConnectionStatus,
    onCancellingModeSwitch
}) => {
    const [errorDescription, visualButtonDisabled] = useMemo<[string | undefined, boolean]>(() => {
        if (!areConnectionFieldsValid) return [`Select schemas and tables visually. `, true];
        if (tablesError) return [`Select at least one ${entityType} to continue.`, false];

        switch (testConnectionStatus) {
            case TestConnectionStatus.Fail:
                return [`Fix the errors in the connection details above to select ${entityType}s visually.`, true];
            case TestConnectionStatus.DriverNotFound:
                return ["Driver not found. Please correct the Connection URL or choose another Adapter", true];
            default:
                return [undefined, false];
        }
    }, [areConnectionFieldsValid, tablesError, testConnectionStatus]);

    return showWarning ? (
        <WarningView
            onEdit={() => {
                onSwitchToVisualMode();
                onEdit();
            }}
            onCancel={onCancellingModeSwitch}
            entityType={entityType}
            enableAction={enableAction}
            tooltip={errorDescription}
            disabled={visualButtonDisabled}
            previousTab={previousTab}
        />
    ) : error ? (
        <ErrorView
            onRetry={onEdit}
            error={error}
            enableAction={enableAction}
            tooltip={errorDescription}
            disabled={visualButtonDisabled}
        />
    ) : success ? (
        <SuccessView
            onEdit={onEdit}
            enableAction={enableAction}
            tooltip={errorDescription}
            disabled={visualButtonDisabled}
        />
    ) : (
        <InitialView
            enableAction={enableAction}
            onClickAction={onClickAction}
            entityType={entityType}
            schemaType={schemaType}
            adapterName={adapterName}
            tablesError={tablesError}
            errorDescription={errorDescription}
            disabled={visualButtonDisabled}
            areConnectionFieldsValid={areConnectionFieldsValid}
        />
    );
};

export default ContentVisualSelection;
